import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { gamificationService, type DailyGoals } from "@/services/gamificationService";
import { useAuth } from "@/contexts/AuthContext";
import { 
  Flame, Trophy, Star, Zap, Award, ChevronDown, ChevronUp,
  Target, TrendingUp, Clock, Sparkles, Shield
} from "lucide-react";

export function ExpandableGamificationCard() {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [dailyGoals, setDailyGoals] = useState<DailyGoals | null>(null);

  const loadDailyGoals = useCallback(() => {
    if (user?.id) {
      const goals = gamificationService.getDailyGoals(user.id);
      setDailyGoals(goals);
    }
  }, [user?.id]);

  useEffect(() => {
    loadDailyGoals();
    const interval = setInterval(loadDailyGoals, 30000);
    return () => clearInterval(interval);
  }, [loadDailyGoals]);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["game-stats", user?.id],
    queryFn: () => gamificationService.getStats(user!.id),
    enabled: !!user?.id,
  });

  const { data: achievements } = useQuery({
    queryKey: ["achievements"],
    queryFn: () => gamificationService.getAchievements(),
  });

  const { data: userAchievements } = useQuery({
    queryKey: ["user-achievements", user?.id],
    queryFn: () => gamificationService.getUserAchievements(user!.id),
    enabled: !!user?.id,
  });

  const handleUseStreakFreeze = () => {
    if (user?.id) {
      const success = gamificationService.useStreakFreeze(user.id);
      if (success) {
        loadDailyGoals();
      }
    }
  };

  const currentXP = parseInt(stats?.xp || "0", 10);
  const currentLevel = parseInt(stats?.level || "1", 10);
  const currentStreak = parseInt(stats?.currentStreak || "0", 10);
  const longestStreak = parseInt(stats?.longestStreak || "0", 10);
  const totalSessions = parseInt(stats?.totalSessions || "0", 10);
  const totalCardsReviewed = parseInt(stats?.totalCardsReviewed || "0", 10);
  const totalQuizzesTaken = parseInt(stats?.totalQuizzesTaken || "0", 10);
  const totalWorkoutsCompleted = parseInt(stats?.totalWorkoutsCompleted || "0", 10);
  const xpProgress = gamificationService.getXPForNextLevel(currentXP);

  const unlockedAchievementIds = new Set(userAchievements?.map(ua => ua.achievementId) || []);

  const dailyProgress = dailyGoals && user?.id
    ? gamificationService.getDailyGoalProgress(user.id).percentage 
    : 0;

  const getStreakColor = (streak: number) => {
    if (streak >= 30) return "text-purple-500";
    if (streak >= 14) return "text-orange-500";
    if (streak >= 7) return "text-amber-500";
    if (streak > 0) return "text-yellow-500";
    return "text-muted-foreground";
  };

  const getStreakMessage = (streak: number) => {
    if (streak >= 30) return "Legendary!";
    if (streak >= 14) return "On fire!";
    if (streak >= 7) return "Great momentum!";
    if (streak >= 3) return "Building habits!";
    if (streak > 0) return "Keep it up!";
    return "Start your streak!";
  };

  if (statsLoading) {
    return (
      <Card className="animate-pulse" data-testid="gamification-card-loading">
        <CardContent className="pt-4">
          <div className="space-y-3">
            <div className="h-16 bg-muted rounded" />
            <div className="h-8 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-visible" data-testid="gamification-card">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0 flex-shrink">
              <div className="relative flex-shrink-0" data-testid="display-level-badge">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-lg">{currentLevel}</span>
                </div>
                <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-0.5 shadow">
                  <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                </div>
              </div>
              <div className="min-w-0">
                <div className="font-semibold" data-testid="text-level">Level {currentLevel}</div>
                <div className="text-sm text-muted-foreground" data-testid="text-xp">
                  {currentXP.toLocaleString()} XP
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 flex-shrink-0">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 cursor-default" data-testid="display-streak">
                      <Flame className={`w-5 h-5 ${getStreakColor(currentStreak)}`} />
                      <span className="font-bold text-lg" data-testid="text-streak-count">{currentStreak}</span>
                      <span className="text-xs text-muted-foreground hidden sm:inline">day streak</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-sm font-medium">{getStreakMessage(currentStreak)}</div>
                    <div className="text-xs text-muted-foreground">Best: {longestStreak} days</div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 cursor-default" data-testid="display-daily-goal">
                      <Target className="w-4 h-4 text-green-500" />
                      <span className="font-bold" data-testid="text-daily-progress">{dailyProgress}%</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-sm font-medium">Daily Progress</div>
                    {dailyGoals && (
                      <>
                        <div className="text-xs text-muted-foreground">
                          Sessions: {dailyGoals.sessionsCompleted}/{dailyGoals.sessionsTarget}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Cards: {dailyGoals.cardsCompleted}/{dailyGoals.cardsTarget}
                        </div>
                      </>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid="button-expand-gamification"
                >
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5" />
                  ) : (
                    <ChevronDown className="h-5 w-5" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Level {currentLevel + 1}</span>
              <span className="font-medium" data-testid="text-xp-needed">{xpProgress.needed.toLocaleString()} XP to go</span>
            </div>
            <Progress value={xpProgress.progress} className="h-2" data-testid="progress-xp" />
          </div>

          <CollapsibleContent className="mt-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20" data-testid="stat-sessions">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-medium">Sessions</span>
                </div>
                <div className="text-2xl font-bold text-blue-500">{totalSessions}</div>
              </div>

              <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20" data-testid="stat-cards">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-green-500" />
                  <span className="text-xs font-medium">Cards</span>
                </div>
                <div className="text-2xl font-bold text-green-500">{totalCardsReviewed}</div>
              </div>

              <div className="p-3 rounded-lg bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20" data-testid="stat-quizzes">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium">Quizzes</span>
                </div>
                <div className="text-2xl font-bold text-amber-500">{totalQuizzesTaken}</div>
              </div>

              <div className="p-3 rounded-lg bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/20" data-testid="stat-workouts">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-pink-500" />
                  <span className="text-xs font-medium">Workouts</span>
                </div>
                <div className="text-2xl font-bold text-pink-500">{totalWorkoutsCompleted}</div>
              </div>
            </div>

            {dailyGoals && (
              <div className="p-3 rounded-lg bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20" data-testid="streak-freeze-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className={`w-5 h-5 ${dailyGoals.streakFreezeAvailable ? "text-cyan-500" : "text-muted-foreground"}`} />
                    <div>
                      <div className="text-sm font-medium">Streak Freeze</div>
                      <div className="text-xs text-muted-foreground">
                        {dailyGoals.streakFreezeAvailable 
                          ? "Protect your streak if you miss a day" 
                          : `Used this week${dailyGoals.streakFreezeUsedAt ? ` on ${dailyGoals.streakFreezeUsedAt}` : ""}`}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant={dailyGoals.streakFreezeAvailable ? "default" : "outline"}
                    size="sm"
                    disabled={!dailyGoals.streakFreezeAvailable}
                    onClick={handleUseStreakFreeze}
                    data-testid="button-use-streak-freeze"
                  >
                    {dailyGoals.streakFreezeAvailable ? "Use Freeze" : "Used"}
                  </Button>
                </div>
              </div>
            )}

            <div data-testid="achievements-section">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Achievements
                </h4>
                <Badge variant="secondary" className="text-xs" data-testid="text-achievements-count">
                  {userAchievements?.length || 0} / {achievements?.length || 0}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                {achievements?.slice(0, 8).map((achievement) => {
                  const isUnlocked = unlockedAchievementIds.has(achievement.id);
                  return (
                    <TooltipProvider key={achievement.id}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg transition-all overflow-hidden ${
                              isUnlocked
                                ? "bg-gradient-to-br from-amber-400 to-orange-500 shadow-md text-white"
                                : "bg-muted opacity-40"
                            }`}
                            data-testid={`achievement-${achievement.id}`}
                          >
                            {achievement.icon || <Trophy className="w-5 h-5" />}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <div className="font-medium">{achievement.name}</div>
                          <div className="text-xs text-muted-foreground max-w-[200px]">
                            {achievement.description}
                          </div>
                          {isUnlocked && (
                            <Badge className="mt-1" variant="secondary">
                              +{achievement.xpReward} XP
                            </Badge>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
                {achievements && achievements.length > 8 && (
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-xs font-medium" data-testid="text-more-achievements">
                    +{achievements.length - 8}
                  </div>
                )}
              </div>
            </div>

            <div className="pt-2 border-t" data-testid="longest-streak-section">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Longest Streak</span>
                <span className="font-medium flex items-center gap-1" data-testid="text-longest-streak">
                  <Flame className="w-4 h-4 text-orange-500" />
                  {longestStreak} days
                </span>
              </div>
            </div>
          </CollapsibleContent>
        </CardContent>
      </Collapsible>
    </Card>
  );
}
